import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Card, Form, Button, Table, Tabs, Tab, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import { downloadCsv } from '../utils/csv';
import ReportCatalog from './ReportCatalog';
import VentasTab from './VentasTab';
import CobrosTab from './CobrosTab';
import CotizacionesTab from './CotizacionesTab';
import GastosTab from './GastosTab';
import { formatDay, todayDR, firstOfMonthDR } from '../utils/dates';
import PagedRows from './PagedRows';
import { reportName } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);

const firstOfMonth = firstOfMonthDR;
const today = todayDR; // the Dominican day (toISOString is UTC: tomorrow after 8 p.m.)

const TAB_KEYS = ['business', 'cobros', 'cotizaciones', 'gastos', 'dgii', 'catalog'];

const ReportsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // expenses, costs and profit reports are admin-only
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(isAdmin ? 'business' : 'cobros');
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());

  const [sales, setSales] = useState([]);
  const [voided, setVoided] = useState([]);
  const [byClient, setByClient] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [arAging, setArAging] = useState(null);
  const [dso, setDso] = useState(null);
  const [payments, setPayments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collectionsGroupBy, setCollectionsGroupBy] = useState('day');
  const [itbisSummary, setItbisSummary] = useState(null);
  const [customersActivity, setCustomersActivity] = useState(null);
  const [quotesStatus, setQuotesStatus] = useState(null);
  const [expensesSummary, setExpensesSummary] = useState(null);
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [profitMargin, setProfitMargin] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [statement, setStatement] = useState(null);

  const loadRangedReports = (rangeOverride) => {
    const params = rangeOverride || { from, to };
    API.get('/reports/sales', { params }).then(res => setSales(res.data)).catch(err => console.error(err));
    API.get('/reports/voided', { params }).then(res => setVoided(res.data)).catch(err => console.error(err));
    if (isAdmin) API.get('/reports/by-client', { params }).then(res => setByClient(res.data)).catch(err => console.error(err));
    if (isAdmin) API.get('/reports/by-product', { params }).then(res => setByProduct(res.data)).catch(err => console.error(err));
    API.get('/reports/ar-aging').then(res => setArAging(res.data)).catch(err => console.error(err));
    if (isAdmin) API.get('/reports/dso', { params }).then(res => setDso(res.data)).catch(err => console.error(err));
    API.get('/reports/payments', { params }).then(res => setPayments(res.data)).catch(err => console.error(err));
    if (isAdmin) API.get('/reports/collections', { params: { ...params, groupBy: collectionsGroupBy } }).then(res => setCollections(res.data)).catch(err => console.error(err));
    API.get('/reports/itbis-summary', { params }).then(res => setItbisSummary(res.data)).catch(err => console.error(err));
    if (isAdmin) API.get('/reports/customers-activity', { params }).then(res => setCustomersActivity(res.data)).catch(err => console.error(err));
    API.get('/reports/quotes-status', { params }).then(res => setQuotesStatus(res.data)).catch(err => console.error(err));
    if (isAdmin) {
      API.get('/reports/expenses-summary', { params }).then(res => setExpensesSummary(res.data)).catch(err => console.error(err));
      API.get('/reports/expenses-by-category', { params }).then(res => setExpensesByCategory(res.data)).catch(err => console.error(err));
      API.get('/reports/profit-margin', { params }).then(res => setProfitMargin(res.data)).catch(err => console.error(err));
      API.get('/reports/profit-loss', { params }).then(res => setProfitLoss(res.data)).catch(err => console.error(err));
    }
    if (selectedClientId) loadStatement(selectedClientId);
  };

  // Picking a date reloads the reports by itself. Typing one passes through half-written dates (year 0002…), so it
  // waits for a pause and for a complete, sensible range.
  const rangeTimer = useRef(null);
  const changeRange = (next) => {
    if (next.from !== undefined) setFrom(next.from);
    if (next.to !== undefined) setTo(next.to);
    const range = { from, to, ...next };
    clearTimeout(rangeTimer.current);
    if (![range.from, range.to].every(d => /^20\d{2}-\d{2}-\d{2}$/.test(d)) || range.from > range.to) return;
    rangeTimer.current = setTimeout(() => loadRangedReports(range), 400);
  };
  useEffect(() => () => clearTimeout(rangeTimer.current), []);

  // Always the client's whole history, not the shared from/to range above — see the backend route's comment.
  const loadStatement = (clientId) => {
    API.get(`/reports/customer-statement/${clientId}`).then(res => setStatement(res.data)).catch(err => console.error(err));
  };

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    loadStatement(clientId);
  };

  const handleCollectionsGroupByChange = (groupBy) => {
    setCollectionsGroupBy(groupBy);
    if (isAdmin) API.get('/reports/collections', { params: { from, to, groupBy } }).then(res => setCollections(res.data)).catch(err => console.error(err));
  };

  useEffect(() => {
    API.get('/reports/outstanding').then(res => setOutstanding(res.data)).catch(err => console.error(err));
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error(err));
  }, []);

  // Reacts both to the initial page load AND to in-app navigation from the
  // Catálogo de Reportes tab (which jumps here with ?tab=&from=&to=&client=
  // rather than rendering its own copy of each report).
  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    const requestedFrom = searchParams.get('from');
    const requestedTo = searchParams.get('to');
    const requestedClient = searchParams.get('client');

    const rangeFrom = requestedFrom || from;
    const rangeTo = requestedTo || to;

    if (TAB_KEYS.includes(requestedTab) && (isAdmin || !['gastos', 'business'].includes(requestedTab))) setActiveTab(requestedTab);
    if (requestedFrom) setFrom(rangeFrom);
    if (requestedTo) setTo(rangeTo);
    if (requestedClient) setSelectedClientId(requestedClient);

    loadRangedReports({ from: rangeFrom, to: rangeTo });
    if (requestedClient) loadStatement(requestedClient, { from: rangeFrom, to: rangeTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>Reportes</h2>

      {activeTab !== 'catalog' && (
        <Card className="shadow-sm p-3 mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Label>Desde</Form.Label>
              <Form.Control type="date" value={from} onChange={e => changeRange({ from: e.target.value })} />
            </Col>
            <Col md={3}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control type="date" value={to} onChange={e => changeRange({ to: e.target.value })} />
            </Col>
            <Col md={3}>
              <Button variant="outline-primary" onClick={() => loadRangedReports()}>Actualizar</Button>
            </Col>
          </Row>
        </Card>
      )}

      <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} className="mb-3">
        {isAdmin && (
        <Tab eventKey="business" title="Ventas">
          <VentasTab byClient={byClient} byProduct={byProduct} customersActivity={customersActivity} />
        </Tab>
        )}

        <Tab eventKey="cobros" title="Cobros">
          <CobrosTab
            outstanding={outstanding}
            arAging={arAging}
            dso={dso}
            payments={payments}
            collections={collections}
            collectionsGroupBy={collectionsGroupBy}
            onCollectionsGroupByChange={handleCollectionsGroupByChange}
            showCompanyKpis={isAdmin}
            clients={clients}
            selectedClientId={selectedClientId}
            onSelectClient={handleSelectClient}
            statement={statement}
          />
        </Tab>

        <Tab eventKey="cotizaciones" title="Cotizaciones">
          <CotizacionesTab quotesStatus={quotesStatus} />
        </Tab>

        {isAdmin && (
          <Tab eventKey="gastos" title="Gastos y Rentabilidad">
            <GastosTab
              expensesSummary={expensesSummary}
              expensesByCategory={expensesByCategory}
              profitMargin={profitMargin}
              profitLoss={profitLoss}
            />
          </Tab>
        )}

        <Tab eventKey="dgii" title="DGII (606/607/608)">
          <Alert variant="warning" className="mt-3">
            Estas tablas son una vista interna basada en tus facturas, pensadas para revisar los números antes de declarar.
            No están garantizadas como formato exacto de subida para la Oficina Virtual de la DGII — confirma el layout
            contra el instructivo oficial, o revisa si tu proveedor de e-CF (DGMax) genera el archivo 606/607/608 por ti
            directamente.
          </Alert>

          <Card className="shadow-sm p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">607 — Ventas</Card.Title>
              <Button size="sm" variant="outline-secondary" onClick={() => downloadCsv(`${reportName('607 — Ventas')}.csv`, sales)}>Exportar CSV</Button>
            </div>
            <PagedRows rows={sales}>{(rows) => (
              <Table hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>No.</th><th>NCF</th><th>Tipo</th><th>Fecha</th><th>Cliente</th><th>RNC/Cédula</th>
                    <th className="text-end">Subtotal</th><th className="text-end">ITBIS</th><th className="text-end">Total</th><th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s, i) => (
                    <tr key={i}>
                      <td>{s.number}</td><td>{s.ncf || '—'}</td><td>{s.tipoEcf}</td><td>{dateStr(s.date)}</td>
                      <td>{s.client}</td><td>{s.clientRnc || '—'}</td>
                      <td className="text-end">{money(s.subtotal)}</td><td className="text-end">{money(s.itbis)}</td>
                      <td className="text-end">{money(s.total)}</td><td>{s.status}</td>
                    </tr>
                  ))}
                  {sales.length === 0 && <tr><td colSpan={10} className="text-muted text-center">Sin facturas en este rango.</td></tr>}
                </tbody>
              </Table>
            )}</PagedRows>
          </Card>

          <Card className="shadow-sm p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">608 — Comprobantes Anulados</Card.Title>
              <Button size="sm" variant="outline-secondary" onClick={() => downloadCsv(`${reportName('608 — Comprobantes Anulados')}.csv`, voided)}>Exportar CSV</Button>
            </div>
            <PagedRows rows={voided}>{(rows) => (
              <Table hover responsive size="sm" className="mb-0">
                <thead>
                  <tr><th>No.</th><th>NCF</th><th>Tipo</th><th>Anulada</th><th>Cliente</th><th className="text-end">Total</th></tr>
                </thead>
                <tbody>
                  {rows.map((v, i) => (
                    <tr key={i}>
                      <td>{v.number}</td><td>{v.ncf || '—'}</td><td>{v.tipoEcf}</td>
                      <td>{dateStr(v.voidedAt)}</td><td>{v.client}</td><td className="text-end">{money(v.total)}</td>
                    </tr>
                  ))}
                  {voided.length === 0 && <tr><td colSpan={6} className="text-muted text-center">Sin comprobantes anulados en este rango.</td></tr>}
                </tbody>
              </Table>
            )}</PagedRows>
          </Card>

          <Card className="shadow-sm p-3 mb-3">
            <Card.Title>606 — Compras y Gastos</Card.Title>
            <p className="text-muted mb-0">
              Aún no disponible — requiere un módulo de compras/facturas de proveedores que todavía no hemos construido.
              (La página de Gastos registra gastos operativos para rentabilidad interna, no compras a proveedores en
              formato DGII 606.)
            </p>
          </Card>

          <Card className="shadow-sm p-3">
            <Card.Title>Resumen de ITBIS (verificación interna)</Card.Title>
            <Table hover size="sm" className="mb-0">
              <thead><tr><th>Tasa</th><th className="text-end">Base</th><th className="text-end">ITBIS</th></tr></thead>
              <tbody>
                {(itbisSummary?.rows || []).map((r, i) => (
                  <tr key={i}>
                    <td>{r.bucket === 'exento' ? 'Exento' : `${r.bucket}%`}</td>
                    <td className="text-end">{money(r.base)}</td>
                    <td className="text-end">{money(r.itbis)}</td>
                  </tr>
                ))}
                {(itbisSummary?.rows || []).length === 0 && (
                  <tr><td colSpan={3} className="text-muted text-center">Sin datos en este rango.</td></tr>
                )}
              </tbody>
              {(itbisSummary?.rows || []).length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--mm-line)' }}>
                    <td colSpan={2}>Total ITBIS</td>
                    <td className="text-end">{money(itbisSummary?.totalItbis)}</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </Card>
        </Tab>

        <Tab eventKey="catalog" title="Catálogo de Reportes">
          <ReportCatalog />
        </Tab>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
